import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { initials } from "@/lib/format";
import { Users, UserPlus, Loader2, MapPin, CalendarRange } from "lucide-react";

const statusColor = {
  active: "bg-emerald-500/20 text-emerald-300",
  invited: "bg-amber-500/20 text-amber-300",
  disabled: "bg-red-500/20 text-red-300",
};
const modeLabel = { onsite: "On-Site", remote: "Remote", hybrid: "Hybrid" };
const emptyForm = { name: "", email: "", primary_workplace_id: "", work_mode: "onsite", clock_in_time: "", clock_out_time: "" };
const WEEKDAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];

function ScheduleDialog({ emp, onSave, onClose }) {
  const isRemote = emp.work_mode === "remote";
  const [remote, setRemote] = useState(emp.remote_schedule || { days: ["mon", "tue", "wed", "thu", "fri"], start: "09:00", end: "17:00" });
  const [hybrid, setHybrid] = useState(emp.hybrid_schedule || { mon: "onsite", tue: "remote", wed: "onsite", thu: "remote", fri: "remote", sat: "off", sun: "off" });
  const [busy, setBusy] = useState(false);

  const toggleDay = (d) => setRemote((r) => ({ ...r, days: r.days.includes(d) ? r.days.filter((x) => x !== d) : [...r.days, d] }));
  const save = async () => {
    setBusy(true);
    await onSave(isRemote ? { remote_schedule: remote } : { hybrid_schedule: hybrid });
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#121816] border-emerald-500/20" data-testid="schedule-dialog">
        <DialogHeader><DialogTitle>{emp.name} · {isRemote ? "Remote" : "Hybrid"} schedule</DialogTitle></DialogHeader>
        {isRemote ? (
          <div className="space-y-3">
            <div>
              <Label>Working days</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {WEEKDAYS.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-1.5 text-sm cursor-pointer bg-white/5 rounded-lg px-2.5 py-1.5">
                    <Checkbox checked={remote.days.includes(k)} onCheckedChange={() => toggleDay(k)} data-testid={`remote-day-${k}`} /> {l}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start</Label><Input type="time" value={remote.start} onChange={(e) => setRemote({ ...remote, start: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="remote-start" /></div>
              <div><Label>End</Label><Input type="time" value={remote.end} onChange={(e) => setRemote({ ...remote, end: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="remote-end" /></div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {WEEKDAYS.map(([k, l]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="text-sm w-14">{l}</span>
                <Select value={hybrid[k] || "off"} onValueChange={(v) => setHybrid({ ...hybrid, [k]: v })}>
                  <SelectTrigger className="w-40 bg-[#0A0D0C] border-emerald-500/20 h-9" data-testid={`hybrid-${k}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121816] border-emerald-500/20">
                    <SelectItem value="onsite">On-Site</SelectItem>
                    <SelectItem value="remote">Remote</SelectItem>
                    <SelectItem value="off">Off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button onClick={save} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="schedule-save-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Employees() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [schedEmp, setSchedEmp] = useState(null);

  const load = async () => {
    const [e, w] = await Promise.allSettled([api.get("/employees"), api.get("/workplaces")]);
    if (e.status === "fulfilled") setList(e.value.data);
    if (w.status === "fulfilled") setWorkplaces(w.value.data);
  };
  useEffect(() => { load(); }, []);

  const wpName = (id) => workplaces.find((w) => w.id === id)?.name;

  const invite = async () => {
    setBusy(true);
    try {
      await api.post("/employees", {
        name: form.name, email: form.email, work_mode: form.work_mode,
        primary_workplace_id: form.primary_workplace_id || undefined,
        clock_in_time: form.clock_in_time || undefined,
        clock_out_time: form.clock_out_time || undefined,
      });
      toast.success(`Invitation sent to ${form.email}.`);
      setOpen(false); setForm(emptyForm); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setStatus = async (emp, status) => {
    try { await api.patch(`/employees/${emp.id}`, { status }); toast.success(`${emp.name} is now ${status}.`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const unlock = async (emp) => {
    try { await api.post(`/employees/${emp.id}/unlock`); toast.success(`${emp.name}'s sign-in is unlocked.`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const setField = async (emp, patch) => {
    try { await api.patch(`/employees/${emp.id}`, patch); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const saveSchedule = async (patch) => {
    try { await api.patch(`/employees/${schedEmp.id}`, patch); toast.success("Schedule saved."); setSchedEmp(null); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-emerald-400" />
          <h1 className="font-display text-2xl font-bold">Employees</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="add-employee-btn"><UserPlus className="w-4 h-4 mr-1" /> Add employee</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121816] border-emerald-500/20">
            <DialogHeader><DialogTitle>Invite an employee</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">They'll receive an email with an activation code.</p>
            <div className="space-y-3">
              <div><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="employee-name-input" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="employee-email-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Work mode</Label>
                  <Select value={form.work_mode} onValueChange={(v) => setForm({ ...form, work_mode: v })}>
                    <SelectTrigger className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="employee-workmode-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121816] border-emerald-500/20">
                      <SelectItem value="onsite">On-Site</SelectItem>
                      <SelectItem value="remote">Remote</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Primary workplace</Label>
                  <Select value={form.primary_workplace_id} onValueChange={(v) => setForm({ ...form, primary_workplace_id: v })}>
                    <SelectTrigger className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="employee-workplace-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-[#121816] border-emerald-500/20">
                      {workplaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">On-Site staff clock in via workplace QR + location. Remote/Hybrid staff get a schedule you can set after inviting.</p>
              <div><Label>Clock-in time</Label><Input type="time" value={form.clock_in_time} onChange={(e) => setForm({ ...form, clock_in_time: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="employee-clockin-input" /></div>
              <div><Label>Clock-out time</Label><Input type="time" value={form.clock_out_time} onChange={(e) => setForm({ ...form, clock_out_time: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="employee-clockout-input" /></div>
            </div>
            <DialogFooter>
              <Button onClick={invite} disabled={busy || !form.name || !form.email} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="employee-invite-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send invitation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {list.map((e) => (
          <div key={e.id} className="card-emerald p-5" data-testid="employee-card">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-300 font-semibold">{initials(e.name)}</div>
              <div className="min-w-0">
                <p className="font-medium truncate">{e.name}</p>
                <p className="text-xs text-muted-foreground truncate">{e.email}</p>
              </div>
              <Badge className={`${statusColor[e.status]} ml-auto`}>{e.status}</Badge>
              {e.security_locked && <Badge className="bg-red-500/20 text-red-300" data-testid="employee-locked-badge">locked</Badge>}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs">
              <Badge variant="outline" className="border-emerald-500/30 text-emerald-200 capitalize">{e.role === "super_admin" ? "Super Admin" : e.role}</Badge>
              <Badge variant="outline" className="border-sky-500/30 text-sky-200">{modeLabel[e.work_mode] || "On-Site"}</Badge>
              {e.primary_workplace_id && <span className="text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{wpName(e.primary_workplace_id) || "—"}</span>}
            </div>
            {(e.job_title || e.id_number) && (
              <div className="mt-2 text-xs text-muted-foreground space-y-0.5" data-testid="employee-job-info">
                {e.job_title && <p className="truncate"><span className="text-emerald-300/80">Role:</span> {e.job_title}</p>}
                {e.id_number && <p className="truncate"><span className="text-emerald-300/80">ID:</span> {e.id_number}</p>}
              </div>
            )}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-muted-foreground">Work mode</Label>
                <Select value={e.work_mode || "onsite"} onValueChange={(v) => setField(e, { work_mode: v })}>
                  <SelectTrigger className="h-8 mt-1 bg-[#0A0D0C] border-emerald-500/20 text-xs" data-testid="employee-set-workmode"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121816] border-emerald-500/20">
                    <SelectItem value="onsite">On-Site</SelectItem>
                    <SelectItem value="remote">Remote</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Primary workplace</Label>
                <Select value={e.primary_workplace_id || ""} onValueChange={(v) => setField(e, { primary_workplace_id: v })}>
                  <SelectTrigger className="h-8 mt-1 bg-[#0A0D0C] border-emerald-500/20 text-xs" data-testid="employee-set-workplace"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#121816] border-emerald-500/20">
                    {workplaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(e.work_mode === "remote" || e.work_mode === "hybrid") && (
              <Button size="sm" variant="outline" className="w-full mt-3 border-emerald-500/30 text-emerald-200" onClick={() => setSchedEmp(e)} data-testid="employee-schedule-btn">
                <CalendarRange className="w-4 h-4 mr-1.5" /> {e.work_mode === "remote" ? "Remote schedule" : "Weekly schedule"}
              </Button>
            )}
            <div className="mt-3 flex gap-2">
              {e.security_locked && (
                <Button size="sm" variant="outline" className="border-amber-500/30 text-amber-300 w-full" onClick={() => unlock(e)} data-testid="unlock-employee-btn">Unlock sign-in</Button>
              )}
              {e.status !== "disabled" ? (
                <Button size="sm" variant="outline" className="border-red-500/30 text-red-300 w-full" onClick={() => setStatus(e, "disabled")} data-testid="disable-employee-btn">Disable access</Button>
              ) : (
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] w-full" onClick={() => setStatus(e, "active")} data-testid="enable-employee-btn">Re-enable</Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {schedEmp && <ScheduleDialog emp={schedEmp} onSave={saveSchedule} onClose={() => setSchedEmp(null)} />}
    </div>
  );
}
